import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { GOOGLE_CLIENT_ID } from './utils/constants';
import { apiClient } from './utils/api';
import { notifySignIn } from './utils/telegram';

// Layout
import AnimatedBackground from './components/layout/AnimatedBackground';
import Navbar from './components/layout/Navbar';
import Sidebar from './components/Sidebar';
import Toast from './components/layout/Toast';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './components/auth/LoginPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import { Monitor, Smartphone, LogOut } from 'lucide-react';
import CreateTestPage from './pages/CreateTestPage';
import TestDetailPage from './pages/TestDetailPage';
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import AdminPage from './pages/AdminPage';
import SharedTestDetailPage from './pages/SharedTestDetailPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DemoDashboard from './pages/DemoDashboard/DemoDashboard';
import ChangelogPage from './pages/ChangelogPage';
import MonsoonCommuteTest from './pages/MonsoonCommuteTest';

// Styles
import './styles/tokens.css';
import './styles/glass.css';
import './styles/animations.css';
import './App.css';

// List of allowed admin emails
export const ADMIN_EMAILS = [
  'fundup3@gmail.com',
  'zaheerkundgol29@gmail.com',
  'zaheer@fundup7.in'
];

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Analytics />
    </GoogleOAuthProvider>
  );
}

// Custom wrapper for TestDetailPage to extract ID from URL if directly visited
function TestDetailWrapper({ selectedTest, company, onBack, showToast }) {
  const { id } = useParams();
  // If we navigated here directly via URL and don't have selectedTest in state,
  // we pass { id } so TestDetailPage can fetch it
  const testToPass = selectedTest || { id }; 
  
  return (
    <TestDetailPage
      test={testToPass}
      company={company}
      onBack={onBack}
      showToast={showToast}
    />
  );
}

// Custom wrapper for SharedTestDetailPage
function SharedTestWrapper({ showToast }) {
  const { token } = useParams();
  return (
    <SharedTestDetailPage
      token={token}
      showToast={showToast}
      onExit={() => { window.location.href = '/'; }}
    />
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [company, setCompany] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [toast, setToast] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const activeCompany = impersonating || company;

  // Derive 'view' string from pathname for backward compatibility with child components
  let view = 'home';
  const p = location.pathname;
  if (p.startsWith('/login')) view = 'login';
  else if (p.startsWith('/dashboard')) view = 'dashboard';
  else if (p.startsWith('/onboarding')) view = 'onboarding';
  else if (p.startsWith('/tests/new')) view = 'create-test';
  else if (p.startsWith('/tests/') && p.length > 7) view = 'test-detail';
  else if (p.startsWith('/tests')) view = 'tests';
  else if (p.startsWith('/reports')) view = 'reports';
  else if (p.startsWith('/settings')) view = 'settings';
  else if (p.startsWith('/admin')) view = 'admin';
  else if (p.startsWith('/shared')) view = 'shared-test';
  else if (p.startsWith('/terms')) view = 'terms';
  else if (p.startsWith('/privacy')) view = 'privacy';
  else if (p.startsWith('/demo')) view = 'demo';
  else if (p.startsWith('/changelog')) view = 'changelog';
  else if (p.startsWith('/challenge')) view = 'challenge';

  // Extract query params for compatibility
  const searchParams = new URLSearchParams(location.search);
  const paramsObj = {};
  for (const [key, value] of searchParams.entries()) {
    paramsObj[key] = value;
  }

  useEffect(() => {
    const saved = localStorage.getItem('bharatqa_company');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCompany(parsed);
      } catch (e) { }
    }
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type, key: Date.now() });
  };

  const handleLogin = async (credentialResponse) => {
    try {
      const data = await apiClient.googleAuth(credentialResponse.credential);
      if (data.success) {
        setCompany(data.company);
        localStorage.setItem('bharatqa_company', JSON.stringify(data.company));
        if (data.token) localStorage.setItem('bharatqa_token', data.token);
        notifySignIn(data.company);
        if (data.company.onboarding_complete) {
          navigate('/dashboard');
        } else {
          navigate('/onboarding');
        }
      } else {
        showToast('Login failed: ' + (data.error || 'Unknown error'), 'error');
      }
    } catch (err) {
      showToast('Login failed: ' + err.message, 'error');
    }
  };

  const handleLogout = () => {
    setCompany(null);
    localStorage.removeItem('bharatqa_company');
    localStorage.removeItem('bharatqa_token');
    navigate('/');
  };

  const updateCompany = (updated) => {
    setCompany(updated);
    localStorage.setItem('bharatqa_company', JSON.stringify(updated));
  };

  // Custom navigate function to preserve compatibility with legacy onNavigate(view) calls
  const legacyNavigate = (newView, params = {}) => {
    if (newView === 'home') navigate('/');
    else if (newView === 'login') navigate('/login' + (params.plan ? `?plan=${params.plan}&price=${params.price}` : ''));
    else if (newView === 'dashboard') navigate('/dashboard');
    else if (newView === 'onboarding') navigate('/onboarding');
    else if (newView === 'tests') navigate('/tests');
    else if (newView === 'create-test') navigate('/tests/new');
    else if (newView === 'test-detail') {
      if (params.test) setSelectedTest(params.test);
      navigate(`/tests/${params.test?.id || params.id}`);
    }
    else if (newView === 'reports') navigate('/reports');
    else if (newView === 'settings') navigate('/settings');
    else if (newView === 'admin') navigate('/admin');
    else if (newView === 'terms') navigate('/terms');
    else if (newView === 'privacy') navigate('/privacy');
    else if (newView === 'demo') navigate('/demo');
    else if (newView === 'changelog') navigate('/changelog');
    else if (newView === 'challenge') navigate('/challenge');
    else if (newView === 'shared-test') navigate(`/shared/${params.token}`);
  };

  // Wrap setSelectedTest to also navigate
  const handleSelectTest = (test) => {
    setSelectedTest(test);
    navigate(`/tests/${test.id}`);
  };

  // Public pages (no sidebar)
  const isPublicPage = ['home', 'login', 'onboarding', 'shared-test', 'terms', 'privacy', 'demo', 'changelog', 'challenge'].includes(view);
  const isCompanyOnboardingComplete = activeCompany && activeCompany.onboarding_complete;
  const showMobileBlock = isMobile && isCompanyOnboardingComplete && !isPublicPage && !ADMIN_EMAILS.includes(activeCompany.email || '');

  return (
    <div className="app-container">
      <AnimatedBackground />

      {toast && (
        <Toast
          key={toast.key}
          message={toast.message}
          type={toast.type}
          onDone={() => setToast(null)}
        />
      )}

      {showMobileBlock ? (
        <div className="mobile-block-container" style={{
          height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '32px', textAlign: 'center',
          background: '#0A0E13', zIndex: 9999, position: 'fixed', inset: 0
        }}>
          <div style={{
            width: '80px', height: '80px', borderRadius: '24px', background: 'var(--saffron-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--saffron)',
            marginBottom: '32px', border: '1px solid rgba(249, 115, 22, 0.2)', boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <Monitor size={40} />
          </div>
          <h2 style={{ fontSize: '28px', fontWeight: 800, marginBottom: '16px', color: '#fff' }}>Use a computer</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, maxWidth: '300px', marginBottom: '40px' }}>
            The BharatQA Dashboard is built for large screens. Please sign in on a
            <strong> desktop or laptop</strong> to manage your tests and view reports.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '280px' }}>
            <button onClick={() => window.location.reload()} className="admin-action-btn unban" style={{ width: '100%', justifyContent: 'center', padding: '14px', borderRadius: '12px' }}>
              Retry Connection
            </button>
            <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', padding: '14px', borderRadius: '12px', color: '#fff', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <LogOut size={16} /> Sign Out
            </button>
          </div>
          <div style={{ position: 'absolute', bottom: '32px', color: 'rgba(255,255,255,0.2)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Smartphone size={14} /> Optimized for Mobile App (Testers)
          </div>
        </div>
      ) : !isPublicPage ? (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
          <Sidebar
            view={view}
            onNavigate={legacyNavigate}
            onLogout={handleLogout}
            company={activeCompany}
          />
          <div style={{ marginLeft: '220px', flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)", backgroundSize: "24px 24px", position: 'relative' }}>
            <div style={{ position: "fixed", top: "-150px", left: "220px", width: "500px", height: "500px", background: "radial-gradient(ellipse, rgba(249,115,22,0.07) 0%, transparent 60%)", pointerEvents: "none", zIndex: 0 }} />
            <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Navbar view={view} onNavigate={legacyNavigate} />
              <main className="main-content" style={{ flex: 1, overflowY: 'auto', background: '#0A0E13' }}>
                <div className="main-content-inner">
                  {impersonating && (
                    <div style={{ background: '#ff4d4d', color: '#fff', padding: '12px', textAlign: 'center', zIndex: 1000, position: 'relative', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                      <span>⚠️ You are impersonating <strong>{impersonating.company_name}</strong></span>
                      <button onClick={() => { setImpersonating(null); navigate('/admin'); }} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.3)', padding: '4px 12px', borderRadius: '4px', color: '#fff', cursor: 'pointer', fontSize: '0.85rem' }}>
                        Exit Impersonation
                      </button>
                    </div>
                  )}

                  <Routes>
                    <Route path="/dashboard" element={activeCompany?.onboarding_complete ? <DashboardPage company={activeCompany} onSelectTest={handleSelectTest} onViewChange={legacyNavigate} showToast={showToast} /> : <Navigate to="/onboarding" />} />
                    <Route path="/tests" element={activeCompany?.onboarding_complete ? <DashboardPage company={activeCompany} onSelectTest={handleSelectTest} onViewChange={legacyNavigate} showToast={showToast} /> : <Navigate to="/onboarding" />} />
                    <Route path="/tests/new" element={activeCompany ? <CreateTestPage company={activeCompany} onClose={() => navigate('/dashboard')} showToast={showToast} /> : <Navigate to="/login" />} />
                    <Route path="/tests/:id" element={activeCompany ? <TestDetailWrapper selectedTest={selectedTest} company={activeCompany} onBack={() => navigate('/tests')} showToast={showToast} /> : <Navigate to="/login" />} />
                    <Route path="/reports" element={activeCompany ? <ReportsPage company={activeCompany} showToast={showToast} /> : <Navigate to="/login" />} />
                    <Route path="/settings" element={activeCompany ? <SettingsPage company={activeCompany} onUpdate={updateCompany} onLogout={handleLogout} showToast={showToast} /> : <Navigate to="/login" />} />
                    
                    <Route path="/admin" element={
                      company && ADMIN_EMAILS.includes(company.email) ? (
                        <AdminPage
                          company={company}
                          showToast={showToast}
                          onImpersonate={async (companyId) => {
                            try {
                              const data = await apiClient.adminGetCompanyProfile(companyId);
                              setImpersonating(data.company);
                              navigate('/dashboard');
                              showToast(`Impersonating ${data.company.company_name} 🕵️`);
                            } catch (err) {
                              showToast('Failed to impersonate: ' + err.message, 'error');
                            }
                          }}
                        />
                      ) : (
                        <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔐</div>
                          <h2>Access Denied</h2>
                          <p>You do not have permission to access the administration panel.</p>
                          <button onClick={() => navigate('/dashboard')} style={{ marginTop: '20px', background: 'var(--saffron)', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                            Back to Dashboard
                          </button>
                        </div>
                      )
                    } />
                  </Routes>

                </div>
              </main>
            </div>
          </div>
        </div>
      ) : (
        <main className="main-content-full">
          <Routes>
            <Route path="/" element={<HomePage company={activeCompany} onLogin={handleLogin} onNavigate={legacyNavigate} />} />
            <Route path="/login" element={<LoginPage onLogin={handleLogin} params={paramsObj} onNavigate={legacyNavigate} />} />
            <Route path="/onboarding" element={activeCompany && !activeCompany.onboarding_complete ? <OnboardingPage company={activeCompany} onComplete={(updated) => { updateCompany(updated); navigate('/dashboard'); }} /> : <Navigate to={activeCompany ? "/dashboard" : "/login"} />} />
            <Route path="/shared/:token" element={<SharedTestWrapper showToast={showToast} />} />
            <Route path="/terms" element={<TermsOfService onNavigate={legacyNavigate} company={activeCompany} />} />
            <Route path="/privacy" element={<PrivacyPolicy onNavigate={legacyNavigate} company={activeCompany} />} />
            <Route path="/demo" element={<DemoDashboard />} />
            <Route path="/changelog" element={<ChangelogPage company={activeCompany} onNavigate={legacyNavigate} />} />
            <Route path="/challenge" element={<MonsoonCommuteTest company={activeCompany} onNavigate={legacyNavigate} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      )}
    </div>
  );
}

export default App;